import { createFileRoute, redirect, useRouteContext } from "@tanstack/react-router";
import { useAtomValue } from "jotai";

import { graphql } from "@repo/graphql-types/gql";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import Gift from "@repo/icons/default/Gift";
import Left from "@repo/icons/default/Left";
import { CpsAlert, CpsBadge } from "corpus";

import { AmountTitle } from "@/components/ui/amount-title";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Item, ItemContent, ItemMainContent, ListRoot } from "@/components/ui/list";
import { Page } from "@/components/ui/layout/page/page";
import { ContactDirection } from "@/enums/customer-contact/contact-direction";
import { ContactType } from "@/enums/customer-contact/contact-type";
import { OriginChannelType } from "@/enums/customer-contact/origin-channel-type";
import { useGraphQL } from "@/hooks/use-graphql";
import { isProUserWithActiveTrial } from "@/lib/assist-guard";
import {
  getAssistVariablePrices,
  getContactPhoneNumber,
  getContactTypeLabel,
} from "@/lib/assist-utils";
import { amountVisibilityAtom } from "@/lib/atoms/amount-visibility";
import { FIVE_MINUTES_STALE_TIME } from "@/lib/constants/dashboards-customer-overview";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";
import {
  convertDateTimeIgnoringTimezone,
  formatDateAndTimeIgnoringTimezone,
  formatIgnoringTimezone,
  formatWithZonedDate,
  getTimeDateFromStrings,
} from "@/lib/helpers/date";

import type { JSX } from "react";

const TrialContactsHistoryPageQuery = graphql(/* GraphQL */ `
  query TrialContactsHistoryPageQuery($from: timestamptz!, $to: timestamptz!, $codPacoteServicos: Int!) {
    nuntius_contato(
      where: {
        cobravel: { _eq: false },
        valido: { _eq: true },
        inicio: { _gte: $from },
        fim: { _lte: $to }
      },
      order_by: { inicio: desc }
    ) {
      id
      direcaoAtendimentoId
      tipoAtendimentoId
      tipoCanalOrigemId
      numeroOrigem
      numeroDestino
      inicio
      fim
      pacienteAtendimento {
        nome
      }
    }

    pacotesServicos_by_pk(codPacoteServicos: $codPacoteServicos) {
      pacotesServicosPreco {
        pacotesServicosPrecosVariaveis {
          valorUnitario
          codPacoteServicosPrecoVariavel
        }
      }
    }
  }
`);

interface TrialContact {
  id: number;
  direcaoAtendimentoId: number;
  tipoAtendimentoId: number;
  tipoCanalOrigemId: number;
  numeroOrigem: string;
  numeroDestino: string;
  inicio: string;
  fim: string;
  pacienteAtendimento?: {
    nome: string;
  } | null;
}

const getDayLabel = (dateString: string): string => {
  const convertedDate = convertDateTimeIgnoringTimezone(dateString, "00:00");

  const weekday = formatIgnoringTimezone(convertedDate, "EEE").slice(0, 3);
  const formattedDate = formatIgnoringTimezone(convertedDate, "dd/MM/yyyy");

  return `${weekday}, ${formattedDate}`;
};

interface ContactsByDay {
  date: string;
  contacts: TrialContact[];
}

const groupContactsByDay = (contacts: TrialContact[]): ContactsByDay[] => {
  const groupedMap = new Map<string, TrialContact[]>();

  for (const contact of contacts) {
    const date = formatDateAndTimeIgnoringTimezone(contact.inicio, "00:00", "yyyy-MM-dd");
    const existing = groupedMap.get(date) ?? [];
    groupedMap.set(date, [...existing, contact]);
  }

  return Array.from(groupedMap.entries())
    .map(([date, dayContacts]) => ({
      date,
      contacts: dayContacts,
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

interface TrialContactItemProps {
  contact: TrialContact;
}

const TrialContactItem = ({ contact }: TrialContactItemProps): JSX.Element => {
  const patientName =
    contact.pacienteAtendimento?.nome ?? "Não está na sua lista de pacientes";
  const contactType = getContactTypeLabel(contact.tipoAtendimentoId as ContactType);
  const startTime = formatWithZonedDate(String(contact.inicio), "HH:mm");
  const endTime = formatWithZonedDate(String(contact.fim), "HH:mm");
  const contactDirection = ContactDirection[contact.direcaoAtendimentoId];
  const contactChannelType = OriginChannelType[contact.tipoCanalOrigemId];
  const phoneNumber = getContactPhoneNumber(
    contact.numeroOrigem,
    contact.numeroDestino,
    contact.direcaoAtendimentoId,
  );

  return (
    <Item
      linkProps={{
        to: "/settings/assist/contacts-history/trial-period/$contactId",
        params: { contactId: contact.id },
      }}
    >
      <ItemContent>
        <div className="flex gap-2">
          <p className="text-xl text-neutral-600 font-medium mb-2">{phoneNumber}</p>

          <CpsBadge variant="square" shade="light" className="mt-1">
            {contactType}
          </CpsBadge>
        </div>
        <ItemMainContent className="text-sm text-neutral-600">
          {patientName}
        </ItemMainContent>
        <p className="text-sm text-neutral-500">
          {`${startTime} - ${endTime}`} • {contactChannelType} • {contactDirection}
        </p>
      </ItemContent>
    </Item>
  );
};

interface TrialContactsDayGroupProps {
  dayData: ContactsByDay;
}

const TrialContactsDayGroup = ({ dayData }: TrialContactsDayGroupProps): JSX.Element => {
  return (
    <div className="border-b-2 border-gray-100">
      <h3 className="font-bold text-lg text-neutral-500 capitalize">
        {getDayLabel(dayData.date)}
      </h3>

      <ListRoot>
        {dayData.contacts.map((contact) => (
          <TrialContactItem key={contact.id} contact={contact} />
        ))}
      </ListRoot>
    </div>
  );
};

const TrialContactsHistoryPage = (): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });
  const isAmountVisible = useAtomValue(amountVisibilityAtom);

  const assistProPackage = user.pacotesServicos.find(
    (servicePkg) => servicePkg.codPacoteServicos === ServicePackageEnum.Pro.valueOf(),
  );

  const trialStartDate = assistProPackage?.dataContratacao
    ? getTimeDateFromStrings(assistProPackage.dataContratacao, "00:00")
        .toISOString()
        .slice(0, 19)
    : "";

  const trialEndDate = assistProPackage?.dataFimTrial
    ? getTimeDateFromStrings(assistProPackage.dataFimTrial, "00:00")
        .toISOString()
        .slice(0, 19)
    : "";

  const queryResult = useGraphQL(
    TrialContactsHistoryPageQuery,
    {
      from: trialStartDate,
      to: trialEndDate,
      codPacoteServicos: ServicePackageEnum.Pro.valueOf(),
    },
    {
      staleTime: FIVE_MINUTES_STALE_TIME,
      enabled: Boolean(trialStartDate && trialEndDate),
    },
  );

  const { data } = queryResult;

  const contacts = (data?.nuntius_contato ?? []) as TrialContact[];
  const contactsByDay = groupContactsByDay(contacts);

  const servicePackagePrices = data?.pacotesServicos_by_pk?.pacotesServicosPreco;
  const variablePrices = (servicePackagePrices?.pacotesServicosPrecosVariaveis ?? []) as {
    codPacoteServicosPrecoVariavel: number;
    valorUnitario: number;
  }[];

  const { digitalContactVariablePrice, humanContactVariablePrice } =
    getAssistVariablePrices(variablePrices);

  const totalAmount = contacts.reduce((acc: number, contact: TrialContact) => {
    const contactType = contact.tipoAtendimentoId;
    const price =
      contactType === ContactType.Digital.valueOf()
        ? (digitalContactVariablePrice ?? 0)
        : (humanContactVariablePrice ?? 0);
    return acc + price;
  }, 0);

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Raio-X de Contatos - Trial" />
      </HeaderRoot>

      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <AmountTitle
                    title="Valor do período"
                    amount={totalAmount * 100}
                    lineThroughAmount
                  />
                  {isAmountVisible ? (
                    <div className="flex items-center gap-2 text-secondary-500">
                      <Gift size={20} className="fill-secondary-500 shrink-0" />
                      <p className="text-sm font-medium">
                        Não se preocupe, esse valor é um presente da Livance para você!
                      </p>
                    </div>
                  ) : null}
                </div>

                <CpsAlert
                  type="info"
                  title="Você está no trial."
                  description="Nenhum contato será cobrado agora. Depois do trial, aparecerão aqui apenas os contatos cobrados."
                />
              </div>

              {contactsByDay.length > 0 ? (
                <div className="flex flex-col gap-6 mt-4">
                  {contactsByDay.map((dayData) => (
                    <TrialContactsDayGroup key={dayData.date} dayData={dayData} />
                  ))}
                </div>
              ) : (
                <EmptyStateRoot id="trial-contacts-history-empty-state">
                  <EmptyStateIcon icon={ExclamationCircle} />
                  <EmptyStateTitle id="trial-contacts-history-empty-state-title">
                    Nenhum contato atendido
                  </EmptyStateTitle>
                  <EmptyStateDescription id="trial-contacts-history-empty-state-description">
                    Os dados podem levar até 48h para aparecer. Aguarde um pouco e tente
                    novamente.
                  </EmptyStateDescription>
                </EmptyStateRoot>
              )}
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/contacts-history/trial-period/")({
  component: TrialContactsHistoryPage,
  beforeLoad: ({ context }) => {
    const { user, flags } = context;
    const showContactHistoryTrialItem =
      flags["habilita-historico-de-contatos-no-menu-do-assist-trial"];

    if (!isProUserWithActiveTrial(user) || !showContactHistoryTrialItem) {
      redirect({
        to: "/settings/assist/contacts-history",
        throw: true,
      });
    }
  },
});
